import { CustomReactPlayerMethods } from "app/components/videos/CustomReactPlayer";
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import CommentSection from 'app/components/layouts/Comments/CommentSection';
import RightContent from 'app/components/layouts/MenuRight/RightContent';
import { slug, decryptText, vocabularyUrlSlug } from 'app/utils/string';
import FontAwesomeIcon from "app/components/layouts/Icons/FontAwesome";
import GoogleAdsense from 'app/components/layouts/Ads/GoogleAdsense';
import { faHeart } from "@fortawesome/free-solid-svg-icons";
import VideoPlayer from 'app/components/videos/VideoPlayer';
import { withGlobalData } from 'app/utils/withGlobalData';
import { toastSuccess } from 'app/utils/toastHelper';
import ListMusic from 'app/components/music/ListMusic';
import { useState, useEffect, useRef } from 'react';
import * as listenApi from 'app/requests/listens';
import Title from 'app/components/layouts/Title';
import Image from 'app/components/layouts/Image';
import { useTranslation } from 'next-i18next';
import storage from 'app/utils/storage';
import { useRouter } from 'next/router';
import { Song } from 'types/music';
import { NextSeo } from 'next-seo';
import uniq from 'lodash/uniq';
import Link from 'next/link';
import env from '.env.json';

interface ListenDetailProps {
  music: Song;
  songs: Song[];
}

export default function ListenDetail({ music, songs }: ListenDetailProps) {
  if (!music) return <></>
  const videoRef = useRef<CustomReactPlayerMethods | null>(null);
  const { t } = useTranslation();
  const [indexLyricActive, setIndexLyricActive] = useState<number | null>(null);
  const [songIdsLove, setSongIdsLove] = useState<string[]>([]);
  const [showLyrics, setShowLyrics] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [loop, setLoop] = useState(true);
  const router = useRouter()

  useEffect(() => {
    music.lyrics.forEach((lyric, index: number) => {
      const timeLyricNext = music?.lyrics?.length >= index + 1 ? decryptText(music.lyrics[index + 1]?.cc) : null;
      const isActive = Number(decryptText(lyric.cc)) <= currentTime && (currentTime < Number(timeLyricNext) || !timeLyricNext);
      if (isActive && index != indexLyricActive) {
        setIndexLyricActive(index);
        setPositionLyricActive();
      }
    })
  }, [currentTime]);

  useEffect(() => {
    let songIds = storage.get("songIds") || '';
    setSongIdsLove(songIds.split(','));
    setIndexLyricActive(null);
    videoRef?.current?.seekTo(0);
  }, [router.asPath]);

  const setPositionLyricActive = () => {
    setTimeout(() => {
      const lyricIsActive = document.querySelector(".lyric-sentence-active");
      const lyricSong = document.querySelector(".song-detail-lyric");
      const positionLyric = (lyricIsActive?.getBoundingClientRect()?.top! - lyricSong?.getBoundingClientRect()?.top!) - 100;
      lyricSong?.scroll({ top: lyricSong?.scrollTop + positionLyric, behavior: 'smooth' });
    }, 0);
  }

  const handlePlay = (cc: string) => {
    const time = decryptText(cc);
    videoRef?.current?.seekTo(Number(time));
  }

  const loveVideo = async (id: string, isAdd: boolean) => {
    let songIds = storage.get("songIds") || '';
    songIds = songIds.split(',');
    if (isAdd && songIds.includes(id)) return;

    if (isAdd) {
      songIds = uniq([...songIds, id]);
      toastSuccess(t('song.like'));
    } else {
      songIds = songIds.filter(item => item != id);
      toastSuccess(t('song.un_like'));
    }

    setSongIdsLove(songIds);
    songIds = songIds.join();
    storage.set("songIds", songIds);
  }

  return (
    <section className="box-card-area list-test song-detail py-md-2 py-0 position-relative overflow-hidden" id="songPage">
      <NextSeo
        title={t('song.learn', { title: music.title })}
        description={music.title}
      />
      <div className="d-flex">
        <div className="bg-white w-100 full-width mt-3 pl-2" style={{ borderLeft: '1px solid rgba(0,0,0,.15)' }}>
          <Title title={t('song.learn', { title: music.title })} />
          <div className='text-center d-flex'>
            <Link href={`/song/singer/${slug(music.singerName)}`}>
              <span className="text-center d-block mx-auto cursor-pointer text-info mt-2 font-weight-bold">
                {t('song.singer', { name: music.singerName })}
                <Image
                  className="rounded-circle align-middle d-inline-block ml-2 my-auto"
                  alt={music.title} width={45} height={45}
                  src={`${env.URL_API_FILE}/${music.singerImage || music.songImage}`}
                />
              </span>
            </Link>
          </div>
          <div className="song-detail-top mt-2">
            <div className="top-cover">
              <div className="top-left" style={{ width: music.lyrics.length > 0 ? '65%' : '100%' }}>
                <VideoPlayer
                  loop={loop}
                  playerRef={videoRef}
                  url={`${env.URL_API_FILE}/${music.videoUrl}`}
                  setCurrentTime={setCurrentTime}
                />
                <div className="video-button d-flex">
                  <div
                    className="button-like-video cursor-pointer d-flex ml-2"
                    onClick={() => loveVideo(music._id, !songIdsLove.includes(music._id))}
                  >
                    <label htmlFor="heart" className="button-text mr-2 mb-0">
                      {t('song.add_to_favories')}
                    </label>
                    <FontAwesomeIcon
                      className={`heart-video d-block ${songIdsLove.includes(music._id) ? 'text-danger' : 'text-white'}`}
                      icon={faHeart}
                    />
                  </div>
                  <div
                    className="button-loop cursor-pointer d-flex ml-3"
                    onClick={() => setLoop(!loop)}
                  >
                    <label htmlFor="loop" className="button-text mr-2 mb-0">
                      {t(`song.turn_on_playback`)}
                    </label>
                    <input
                      id='loop'
                      type="checkbox"
                      value={loop.toString()}
                      checked={loop}
                      style={{ accentColor: '#990000' }}
                      onChange={() => setLoop(!loop)}
                    />
                  </div>
                  {music.lyrics.length > 0 &&
                    <div className="button-show-lyric cursor-pointer d-flex ml-3">
                      <label htmlFor="lyric" className="button-text mr-2 mb-0">
                        {t(`song.show_lyrics`)}
                      </label>
                      <input
                        id='lyric'
                        type="checkbox"
                        value={showLyrics.toString()}
                        checked={showLyrics}
                        onChange={() => setShowLyrics(!showLyrics)}
                      />
                    </div>
                  }
                </div>
              </div>
              {music.lyrics.length > 0 &&
                <div className="top-right">
                  <div className="top-right-cover">
                    <div className="song-detail-lyric">
                      <div className={`lyric-hide-center ${showLyrics ? 'd-none' : ''}`}>
                        <h5 className="lyric-hide-title">{t('song.title_hide_lyrics')}</h5>
                        <span className="lyric-hide-des" dangerouslySetInnerHTML={{ __html: t('song.des_hide_lyrics') }} />
                      </div>
                      <div className={`song-detail-lyric-cover ${!showLyrics ? 'd-none' : ''}`}>
                        {music.lyrics.map((lyric, index: number) => {
                          return (
                            <span key={index} onClick={() => handlePlay(lyric.cc)}
                              className={`lyric-sentence ${indexLyricActive === index ? 'lyric-sentence-active' : ''}`}>
                              <span className="span-child">{lyric.en}
                                <span className="child-vi">{lyric.translate}</span>
                              </span>
                            </span>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
      <div className="d-flex bg-white" style={{ paddingRight: 15 }}>
        <div className='content_inner box-news-area'>
          <div className="mb-5">
            <h2 className="text-info text-size-2xl">{t('song.vocabulary')}</h2>
            {
              music?.vocabularies?.map((vocabulary: string, index: number) => {
                return (
                  <Link className="pr-2" key={index} href={`/vocabularies/${vocabularyUrlSlug(vocabulary, router?.locale)}`}>
                    <em className='link-primary'>{vocabulary}</em>
                  </Link>
                )
              })
            }
          </div>
          <h2 className="text-info mt-0 mb-3">
            {t('song.random')}
          </h2>
          <GoogleAdsense width="100%" dataSlot="7482232136" />
          <ListMusic musics={songs} pushUrl={() => { }} />
          <CommentSection roomId={`song-id-${music?._id}`} />
        </div>
        <RightContent />
      </div>
    </section>
  )
}
export const getServerSideProps = withGlobalData(async ({ locale, query }) => {
  try {
    const [music, translations, songs] = await Promise.all([
      listenApi.infoSong(query.slug, locale),
      serverSideTranslations(locale, ['common']),
      listenApi.getSongs({ direction: 'asc', limit: 30, type: 'top' }),
    ]);

    if (!music) return { notFound: true };

    return {
      props: {
        ...translations,
        music,
        songs: songs.data || [],
      },
    };
  } catch (error) {
    return { notFound: true };
  }
});

